import { omit } from 'lodash';
import moment from 'moment';
import qs from 'qs';
import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as SplitIcon } from 'src/assets/images/split-icon.svg';
import { DeliveryPriorityData } from 'src/components/delivery-priority-data/delivery-priority-data';
import { highlightSplitOrders } from 'src/components/order-item/order-item-data';
import styled from 'styled-components';
import { ReactComponent as Cross } from '../../../assets/images/cross.svg';
import { DeliveryData } from '../../../components/delivery-data/delivery-data';
import { Quantity } from '../../../components/quantity/quantity';
import { ScheduleData } from '../../../components/schedule-data/schedule-data';
import { StatusBadge } from '../../../components/status-badge/status-badge';
import { URL_DATE_FORMAT } from '../../../constants/dates';
import {
  IDelivery,
  IItem,
  IOrder,
  IScheduleLine,
} from '../../../graphql/types';
import { Black2, Grey } from '../../../styles/colours';
import { DemiBold, Medium } from '../../../styles/fonts';
import { availabilityRoute } from '../../product-availability/route';
import { checkIfRetail } from '../order-summary-card';
import { ORDER_ID_PARAM, orderDetailsRoute } from '../route';

const CloseLink = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  z-index: 20;
`;
const OverlayHeading = styled.h1`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2rem 0;
  margin: 0;
`;

const StyledDescription = styled.div`
  font-family: ${DemiBold};
`;

const StyledIndexNumber = styled.div`
  margin-right: 1rem;
  font-size: 1.5rem;
`;

const OverlaySubHeading = styled.h2`
  font-family: ${DemiBold};
  padding: 2rem 0;
  margin: 0;
`;

const OrderItemDetails = styled.div`
  display: flex;
  flex-direction: row;
  padding: 2rem 0 3rem 0;
  border-bottom: 1px solid #ddd;
`;

const DataCell = styled.div`
  display: flex;
  flex-direction: column;

  padding: 1rem 2rem;

  border-right: 0.5px solid ${Grey};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: none;
  }
`;

const DataLabel = styled.div`
  color: ${Black2};
`;

const DataValue = styled.div`
  font-family: ${Medium};
  font-size: 1.2rem;
  padding-top: 0.5rem;
`;

const SplitOrderIconContainer = styled.div`
  margin-right: 2rem;
`;

const compareDeliveries = (a: IDelivery, b: IDelivery) =>
  moment(a.provenOn, URL_DATE_FORMAT).unix() -
  moment(b.provenOn, URL_DATE_FORMAT).unix();
const compareSchedules = (a: IScheduleLine, b: IScheduleLine) =>
  moment(a.promiseDate, URL_DATE_FORMAT).unix() -
  moment(b.promiseDate, URL_DATE_FORMAT).unix();

export const ItemDetailsOverlayContent: FC<{ order: IOrder; item: IItem }> = ({
  order,
  item,
}) => {
  const location = useLocation();
  const originalParams = omit(
    qs.parse(location.search, { ignoreQueryPrefix: true }),
    'itemId',
    'uid'
  );

  return (
    <>
      <CloseLink>
        <Link
          to={orderDetailsRoute(order.soldTo.id, {
            ...originalParams,
            [ORDER_ID_PARAM]: order.id,
          })}
          data-type="close-more-info"
        >
          <Cross />
        </Link>
      </CloseLink>
      <OverlayHeading>
        {highlightSplitOrders(item) && (
          <SplitOrderIconContainer title="Split Order" data-type="split-order">
            <SplitIcon width="35" height="35" />
          </SplitOrderIconContainer>
        )}
        <StyledIndexNumber data-type="item-id">#{item.index}</StyledIndexNumber>
        <StyledDescription data-type="product-description">
          {item.product.description}
        </StyledDescription>
      </OverlayHeading>
      <OrderItemDetails>
        <DataCell>
          <DataLabel>Product ID</DataLabel>
          <DataValue>
            {order.soldTo.salesOrg === 'SG88' ||
            order.soldTo.salesOrg === 'US86' ||
            order.orderType === 'ZORD' ||
            checkIfRetail(order) ? (
              item.product.id
            ) : (
              <Link
                data-type="product-id"
                to={availabilityRoute(order.shipTo.id, {
                  productId: item.product.id,
                  plantId: item.plant.id,
                })}
              >
                {item.product.id}
              </Link>
            )}
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Total items</DataLabel>
          <DataValue data-type="product-desired-quantity">
            <Quantity
              quantity={item.desiredQuantity}
              unitOfMeasure={item.unitOfMeasure}
            />
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Delivered</DataLabel>
          <DataValue data-type="product-actual-quantity">
            <Quantity
              quantity={item.actualQuantity}
              unitOfMeasure={
                item.deliveries.length > 0
                  ? item.deliveries[0].unitOfMeasure
                  : item.unitOfMeasure
              }
            />
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Back ordered</DataLabel>
          <DataValue data-type="product-backordered-quantity">
            <Quantity
              quantity={item.backorderedQuantity}
              unitOfMeasure={item.unitOfMeasure}
            />
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Delivery priority</DataLabel>
          <DataValue data-type="item-delivery-priority">
            <DeliveryPriorityData item={item} />
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Route code</DataLabel>
          <DataValue data-type="item-route-code">{item.routeCode}</DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Plant</DataLabel>
          <DataValue data-type="order-plant">
            <span>{item.plant.id + '\n' + item.plant.description}</span>
          </DataValue>
        </DataCell>
        <DataCell>
          <DataLabel>Item status</DataLabel>
          <DataValue data-type="item-status">
            <span>
              <StatusBadge status={item.status} />
            </span>
          </DataValue>
        </DataCell>
      </OrderItemDetails>
      <div>
        <OverlaySubHeading>Supply Plan</OverlaySubHeading>

        {item.schedules.length === 0 && (
          <div>
            Scheduled delivery information is not available at this time. Please
            check back later.
          </div>
        )}

        {item.schedules.sort(compareSchedules).map((schedule, index) => (
          <ScheduleData key={index} schedule={schedule} />
        ))}
      </div>
      <div>
        <OverlaySubHeading>Deliveries</OverlaySubHeading>

        {item.deliveries.length === 0 && (
          <div>
            Delivery information is not available at this time. Please check
            back later.
          </div>
        )}

        {item.deliveries.sort(compareDeliveries).map((delivery, index) => (
          <DeliveryData key={index} delivery={delivery} order={order} />
        ))}
      </div>
      {!item.status && (
        <div>
          <OverlaySubHeading>Reason for Rejection</OverlaySubHeading>
          <div>{item.rejectedReasonDesc}</div>
        </div>
      )}
    </>
  );
};
